/**
 * @fileoverview Redux store configuration with Rematch and persistence.
 */

/* eslint-disable @typescript-eslint/no-non-null-assertion */
import {init} from "@rematch/core";
import createLoadingPlugin from "@rematch/loading";
import createRematchPersist, {getPersistor} from "@rematch/persist";
import selectPlugin from "@rematch/select";

import {combineReducers} from "redux";
import {thunk} from "redux-thunk";

import {makeTransform as makeAdmissionUiTransform} from "../models/transforms/admissionUiTransform";
import {model as adminAdmissionExperienceUi} from "../models/AdminAdmissionExperienceUiModel";
import {adminSessionModel} from "../models/admin-session/AdminSessionModel";
import {model as admissionSession} from "../models/admission-session/AdmissionSessionModel";
import {model as admissionExperienceUi} from "../models/AdmissionExperienceUiModel";
import {model as admissions} from "../models/AdmissionsModel";
import {model as locale} from "../models/LocaleModel";
import {model as downloadManager} from "../models/downloadManager";
import {rootReportingReducer} from "./reporting";
import {rootUserPermissionsReducer} from "./user-permissions";
import {ReportAction} from "./reporting/reporting.actions";
import {isDev} from "../config";

/**
 * Map of reducers for the root reducer.
 */
const reducersMap = {
  reporting: rootReportingReducer,
  userPermissions: rootUserPermissionsReducer,
};

/**
 * Root reducer combining all feature reducers.
 */
const rootReducer = combineReducers(reducersMap);

/**
 * Type representing the complete application state.
 */
export type AppState = ReturnType<typeof rootReducer> & {
  adminSession: any; // This is temporary until we migrate to Rematch v2 and use typescript models
};

/**
 * Redux store instance configured with Rematch, persistence, and plugins.
 */
export const store: any = init({
  models: {
    adminAdmissionExperienceUi,
    adminSessionModel,
    admissionSession,
    admissionExperienceUi,
    admissions,
    locale,
    downloadManager,
  },
  plugins: [
    selectPlugin(),
    createLoadingPlugin(),
    createRematchPersist({
      whitelist: [
        adminSessionModel.name,
        admissionExperienceUi.name,
        admissionSession.name,
      ],
      version: 1,
      debug: isDev,
      transforms: [makeAdmissionUiTransform(admissionExperienceUi.name)],
    }),
  ],
  redux: {
    reducers: reducersMap,
    // Fix thunk middleware typing - Redux 5.0 + Rematch v1 compatibility issue.
    middlewares: [thunk as any],
  },
});

/**
 * Persistor instance for handling store persistence.
 */
export const persistor = getPersistor();

/**
 * Type for Redux dispatch function.
 */
export type Dispatch = typeof store.dispatch;

/**
 * Destructured store methods for convenience.
 */
export const {dispatch, getState, select} = store;

/**
 * TypeScript module declaration for typesafe-actions integration.
 */
declare module "typesafe-actions" {
  interface Types {
    RootAction: ReportAction;
  }
}
